import React, { useState, useMemo, useEffect } from 'react';
import { 
  ArrowRight, 
  Cpu, 
  Workflow, 
  Bot, 
  Mic2, 
  ShieldCheck, 
  Star, 
  Briefcase, 
  X, 
  MessageSquare, 
  Copy, 
  Send, 
  Check, 
  ExternalLink, 
  Target, 
  Zap, 
  Code2, 
  Clock, 
  ShieldAlert, 
  Search,
  BookOpen,
  ChevronLeft,
  Maximize2,
  Crown,
  Quote,
  ChevronDown,
  AlertCircle,
  FileText,
  Calendar,
  Timer,
  User
} from 'lucide-react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import QualificationForm from './QualificationForm';

// --- Data ---
const LOGO_URL = "https://res.cloudinary.com/db3fbeqb8/image/upload/v1766131661/Gemini_Generated_Image_hgz61dhgz61dhgz6_gy3ftg.png";
const CONTACT_EMAIL = "devon@metaknow.org";
const UPWORK_URL = "https://www.upwork.com/freelancers/devonjwilliams";

const COMPETENCIES = [
  {
    title: "CUSTOM AI AGENTS",
    description: "Our autonomous agents are engineered for deep operational integration. Unlike brittle bots, we build self correcting architects that manage multi step B2B processes with surgical precision, ensuring absolute reliability across your enterprise stack.",
    icon: <Bot className="w-6 h-6 text-brand-green" />
  },
  {
    title: "VOICE AI TECH",
    description: "Deploy high fidelity, humanlike voice interfaces that redefine engagement. We architect real time systems that move beyond basic scripts, delivering natural interactions that represent your brand perfectly while scaling communication capacity without adding headcount.",
    icon: <Mic2 className="w-6 h-6 text-brand-green" />
  },
  {
    title: "WORKFLOW AUTOMATION",
    description: "We orchestrate complex data pipelines into seamless process architectures. Our approach ensures your business ecosystem operates as a unified engine, eliminating silos through intelligent logic flows that adapt to your growth and drive measurable efficiency.",
    icon: <Workflow className="w-6 h-6 text-brand-green" />
  },
  {
    title: "SYSTEM SECURITY",
    description: "Security is our architecture's foundation. We implement enterprise grade guardrails and secure data handling protocols to ensure your proprietary intelligence remains private, compliant, and protected against the evolving landscape of AI specific vulnerabilities.",
    icon: <ShieldCheck className="w-6 h-6 text-brand-green" />
  }
];

const PROCESS_STEPS = [
  {
    number: "01",
    title: "VISION DISCOVERY",
    description: "We move beyond surface level requirements. Our architects conduct deep dive sessions to define your core business objectives, identifying the specific vision of success that anchors the entire technical architecture.",
    icon: <Target className="w-5 h-5" />
  },
  {
    number: "02",
    title: "REVERSE ENGINEERING",
    description: "We map the ideal final state back to the present. We engineer the logic paths, data dependencies, and system triggers required to ensure a frictionless transition from legacy workflows to AI augmented operations.",
    icon: <Search className="w-5 h-5" />
  },
  {
    number: "03",
    title: "ARCHITECTURE & BUILD",
    description: "Systems are constructed using a modular, enterprise grade framework. We deploy custom agents and integrated pipelines with precision, ensuring every component is optimized for high scalability and secure data handling.",
    icon: <Code2 className="w-5 h-5" />
  },
  {
    number: "04",
    title: "SUCCESS VALIDATION",
    description: "No solution is deployed without rigorous multi layer testing. We validate every deliverable against your initial success criteria, ensuring the system outperforms expectations and achieves the defined business vision.",
    icon: <ShieldCheck className="w-5 h-5" />
  }
];

const PORTFOLIO = [
  {
    title: "AI VOICE AGENT FOR LAW OFFICE",
    description: "An elite autonomous receptionist capable of handling complex inbound legal inquiries 24/7. This system utilizes low latency voice synthesis and multi step reasoning to qualify leads before they ever reach a human clerk.",
    challenge: "High call volume leading to missed consultations and slow lead response times.",
    solution: "A VAPI powered voice architect with native N8N logic processing and real time CRM synchronization.",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=1200",
    tags: ["VAPI", "N8N", "LEGAL TECH", "VOICE AI"]
  },
  {
    title: "ADVANCED CRM DATA ORCHESTRATION",
    description: "Automated referral tracking and performance breakdown dashboards for enterprise scale sales operations. We transformed a brittle manual spreadsheet into a self healing data pipeline.",
    challenge: "Data silos across multiple departments resulting in inconsistent reporting and lost revenue.",
    solution: "Custom Google Apps Script architecture with real time validation layers and automated Slack alerting.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200",
    tags: ["APPS SCRIPT", "GOOGLE SHEETS", "SALES OPS", "AUTOMATION"]
  },
  {
    title: "GALACTIC EDEN - WEB3 ECOSYSTEM",
    description: "Full stack blockchain architecture featuring smart contract integration, custom wallet connectivity, and a high fidelity visual interface for token presales.",
    challenge: "Building a secure, high trust environment for high frequency crypto transactions.",
    solution: "React based UI with Solana integration and multi layer security audits for smart contract interactions.",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&get=80&w=1200",
    tags: ["SOLANA", "WEB3", "REACT", "BLOCKCHAIN"]
  },
  {
    title: "VITALITY PATIENT JOURNEY AUTOMATION",
    description: "An omnichannel healthcare automation system that manages the entire patient lifecycle from first touch to post care follow up without manual intervention.",
    challenge: "High patient churn due to inconsistent follow up and administrative overhead.",
    solution: "Secure HIPAA compliant integration between Jane App, Mailchimp, and custom SMS gateways.",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=1200",
    tags: ["HEALTH TECH", "CRM", "PATIENT JOURNEY", "OMNICHANNEL"]
  }
];

const METRICS = [
  {
    title: "78% REDUCTION IN OPERATIONAL OVERHEAD",
    quote: "We architected an autonomous B2B agent system that eliminated manual data entry and synchronized complex logistics across the enterprise stack.",
    impact: "PROCESS AUTOMATION",
    date: "Q4 2024"
  },
  {
    title: "$1.2M IN PROJECTED ANNUAL REVENUE GROWTH",
    quote: "By deploying high fidelity voice AI for lead qualification, we enabled the sales team to focus exclusively on high value, closing ready prospects.",
    impact: "REVENUE INCREASE",
    date: "ANNUAL PROJECTED"
  },
  {
    title: "99.9% SYSTEM UPTIME WITH ZERO LOGIC DRIFT",
    quote: "Our architecture first approach ensures that automated pipelines remain resilient under load, preventing brittle failure points common in basic automation.",
    impact: "SYSTEM STABILITY",
    date: "VERIFIED 2025"
  }
];

// --- Animation Variants ---
const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: any = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" as const }
  })
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

// --- Components ---

const MetricModal = ({ metric, isOpen, onClose }: { metric: any; isOpen: boolean; onClose: () => void }) => {
  return (
    <AnimatePresence>
      {isOpen && metric && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/90 backdrop-blur-xl" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 30 }} className="relative w-full max-w-2xl bg-[#0a0a0a] border border-brand-green/20 rounded-[48px] p-10 md:p-16 shadow-[0_0_100px_rgba(51,255,51,0.15)] overflow-hidden">
            <button onClick={onClose} className="absolute top-8 right-8 text-slate-500 hover:text-white transition-colors"><X className="w-8 h-8" /></button>
            <div className="flex flex-col items-start">
              <div className="flex space-x-2 mb-8">{[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-brand-green fill-brand-green" />)}</div>
              <div className="relative mb-12">
                <Quote className="absolute -top-6 -left-8 w-12 h-12 text-brand-green/10" />
                <h2 className="text-2xl md:text-3xl font-medium text-white leading-relaxed relative z-10">{metric.quote}</h2>
              </div>
              <div className="space-y-8 w-full">
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-green mb-3">OUTCOME REACHED</h4>
                  <p className="text-xl md:text-2xl font-black text-white uppercase tracking-tighter leading-none">{metric.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 pt-8 border-t border-white/10">
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 mb-2">DOMAIN</h4>
                    <p className="text-sm font-bold text-slate-300 uppercase tracking-widest">{metric.impact}</p>
                  </div>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 mb-2">VALIDATED</h4>
                    <p className="text-sm font-bold text-slate-300 uppercase tracking-widest">{metric.date}</p>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="mt-12 w-full bg-brand-green/10 border border-brand-green/30 text-brand-green py-5 rounded-3xl text-xs font-black uppercase tracking-[0.2em] hover:bg-brand-green hover:text-black transition-all">RETURN TO SYSTEM</button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const TrustBadges = () => (
  <motion.div variants={fadeIn} className="flex flex-wrap items-center justify-center gap-6 mt-12 mb-4">
    <a href={UPWORK_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 group hover:scale-105 transition-transform">
      <div className="w-8 h-8 rounded-full border-2 border-[#14a800] flex items-center justify-center bg-transparent group-hover:bg-[#14a800]/5 transition-colors"><Crown className="w-4 h-4 text-[#14a800] fill-[#14a800]" /></div>
      <span className="text-[13px] font-black uppercase tracking-tight text-white group-hover:text-brand-green transition-colors">100% Job Success</span>
    </a>
    <a href={UPWORK_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 group hover:scale-105 transition-transform">
      <div className="w-8 h-8 bg-[#14a800] rounded-[6px] flex items-center justify-center group-hover:brightness-110 transition-all shadow-[0_0_15px_rgba(20,168,0,0.3)]"><Star className="w-4 h-4 text-white fill-white" /></div>
      <span className="text-[13px] font-black uppercase tracking-tight text-white group-hover:text-brand-green transition-colors">Top Rated</span>
    </a>
  </motion.div>
);

const ContactModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(CONTACT_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleLaunchEmail = () => { window.location.href = `mailto:${CONTACT_EMAIL}?subject=AI Architecture Inquiry`; };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-center px-4 overflow-y-auto py-12 md:py-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.95, y: 20 }} 
            className="relative w-full max-w-2xl bg-[#0a0a0a] border border-brand-green/30 rounded-[32px] p-6 md:p-10 shadow-[0_0_100px_rgba(51,255,51,0.1)] self-start"
          >
            <button onClick={onClose} className="absolute top-6 right-6 text-slate-500 hover:text-white transition z-10 p-2"><X className="w-6 h-6" /></button>
            
            <QualificationForm />

            <div className="mt-12 pt-8 border-t border-white/5 w-full flex flex-col items-center">
               <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-6">OR CONTACT DIRECTLY</label>
               <div className="flex flex-wrap justify-center gap-4">
                 <button onClick={handleCopy} className="bg-brand-card hover:bg-white/5 border border-white/10 rounded-2xl px-6 py-4 flex items-center space-x-3 transition group">
                   {copied ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4 text-slate-500 group-hover:text-brand-green transition" />}
                   <span className="text-[10px] font-bold uppercase tracking-widest text-white">{copied ? 'COPIED' : 'COPY EMAIL'}</span>
                 </button>
                 <button onClick={handleLaunchEmail} className="bg-brand-card hover:bg-white/5 border border-white/10 rounded-2xl px-6 py-4 flex items-center space-x-3 transition group">
                   <Send className="w-4 h-4 text-slate-500 group-hover:text-brand-green transition" />
                   <span className="text-[10px] font-bold uppercase tracking-widest text-white">LAUNCH APP</span>
                 </button>
               </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const ProjectModal = ({ project, isOpen, onClose }: { project: any; isOpen: boolean; onClose: () => void }) => {
  return (
    <AnimatePresence>
      {isOpen && project && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-6 overflow-y-auto py-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/90 backdrop-blur-md" />
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 30 }} className="relative w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-[48px] overflow-hidden shadow-[0_0_150px_rgba(51,255,51,0.15)]">
            <button onClick={onClose} className="absolute top-8 right-8 z-20 w-12 h-12 bg-black/50 backdrop-blur-md rounded-full border border-white/10 flex items-center justify-center text-white hover:bg-brand-green hover:text-black transition-all duration-300"><X className="w-6 h-6" /></button>
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-1/2 h-80 lg:h-auto overflow-hidden"><img src={project.image} alt={project.title} className="w-full h-full object-cover" /></div>
              <div className="lg:w-1/2 p-10 lg:p-16">
                <div className="flex flex-wrap gap-2 mb-8">{project.tags.map((tag: string) => (<span key={tag} className="text-[10px] font-black uppercase tracking-widest text-brand-green border border-brand-green/30 bg-brand-green/5 px-3 py-1.5 rounded-full">{tag}</span>))}</div>
                <h2 className="text-4xl lg:text-5xl font-black tracking-tighter text-white uppercase leading-[0.9] mb-8">{project.title}</h2>
                <div className="space-y-10">
                  <div><h4 className="text-xs font-black uppercase tracking-[0.3em] text-brand-green mb-3">OVERVIEW</h4><p className="text-slate-300 text-lg leading-relaxed font-medium">{project.description}</p></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div><h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 mb-3">THE CHALLENGE</h4><p className="text-slate-400 text-sm leading-relaxed">{project.challenge}</p></div>
                    <div><h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 mb-3">THE SOLUTION</h4><p className="text-slate-400 text-sm leading-relaxed">{project.solution}</p></div>
                  </div>
                </div>
                <div className="mt-12"><button onClick={onClose} className="w-full bg-brand-green text-black py-5 rounded-3xl text-sm font-black uppercase tracking-widest shadow-[0_10px_30px_rgba(51,255,51,0.2)] flex items-center justify-center space-x-3 group"><span>CLOSE EXPLORER</span><ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></button></div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const TypingWord = ({ word }: { word: string }) => {
  const [displayText, setDisplayText] = useState('');
  const [isBackspacing, setIsBackspacing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    let timeout: any;
    if (isPaused) {
      timeout = setTimeout(() => { setIsPaused(false); setIsBackspacing(!isBackspacing); }, isBackspacing ? 1000 : 3000);
    } else if (isBackspacing) {
      if (displayText.length > 0) { timeout = setTimeout(() => { setDisplayText(displayText.slice(0, -1)); }, 80); } else { setIsPaused(true); }
    } else {
      if (displayText.length < word.length) { timeout = setTimeout(() => { setDisplayText(word.slice(0, displayText.length + 1)); }, 150); } else { setIsPaused(true); }
    }
    return () => clearTimeout(timeout);
  }, [displayText, isBackspacing, isPaused, word]);

  return (
    <span className="relative inline-block text-center">{displayText}<motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.6, repeat: Infinity, ease: "steps(2)" as any }} className="inline-block w-[2px] h-[0.8em] bg-brand-green ml-1 align-middle shadow-[0_0_100px_#33ff33]" /></span>
  );
};

const Header = ({ onContactClick, setView }: { onContactClick: () => void, setView: (v: 'home' | 'blog') => void }) => (
  <motion.header initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: "circOut" }} className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
      <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
        <motion.img whileHover={{ scale: 1.1, rotate: 5 }} src={LOGO_URL} alt="Meta Know Labs Logo" className="w-10 h-10 object-contain drop-shadow-[0_0_8px_rgba(51,255,51,0.3)]" />
        <span className="text-xl font-black tracking-tighter transition-all group-hover:text-brand-green uppercase">META KNOW <span className="text-brand-green">LABS</span></span>
      </div>
      <nav className="hidden lg:flex items-center space-x-8 xl:space-x-12">
        {['Architecture', 'Process', 'Portfolio', 'Insights'].map((item) => (
          <motion.button key={item} onClick={() => {
            if (item === 'Insights') setView('blog');
            else { setView('home'); setTimeout(() => { document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50); }
          }} whileHover={{ scale: 1.05, color: '#fff' }} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 transition">{item}</motion.button>
        ))}
      </nav>
      <div className="flex items-center space-x-4 sm:space-x-6">
        <span className="hidden md:inline-block text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-slate-300">
          Phone: 
        </span>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={onContactClick} className="bg-brand-green text-black px-4 sm:px-6 py-2.5 rounded text-[10px] sm:text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition shadow-[0_0_20px_rgba(51,255,51,0.2)]">REACH OUT</motion.button>
      </div>
    </div>
  </motion.header>
);

const BlogView = ({ setView, onContactClick }: { setView: (v: 'home' | 'blog') => void, onContactClick: () => void }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-32 pb-32 px-4 sm:px-6 max-w-4xl mx-auto overflow-x-hidden">
    <motion.button onClick={() => setView('home')} whileHover={{ x: -5 }} className="flex items-center space-x-2 text-brand-green font-black uppercase tracking-widest text-[10px] sm:text-xs mb-12 group"><ChevronLeft className="w-4 h-4" /><span>BACK TO TERMINAL</span></motion.button>
    
    <article className="prose prose-invert prose-brand max-w-none">
      <header className="mb-20">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-green px-3 py-1 bg-brand-green/5 border border-brand-green/20 rounded-full">STRATEGIC INSIGHT</span>
          <div className="flex items-center space-x-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
            <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" /> FEB 2025</span>
            <span className="flex items-center"><Timer className="w-3 h-3 mr-1" /> 12 MIN READ</span>
          </div>
        </div>
        <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter text-white uppercase leading-[0.9] mb-10 break-words">
          HOW AI AUTOMATIONS ARE <span className="text-brand-green">TRANSFORMING</span> MODERN BUSINESSES
        </h1>
        <p className="text-xl md:text-2xl text-slate-400 font-medium leading-relaxed border-l-4 border-brand-green/30 pl-8 mb-12">
          Most businesses don’t have a “talent” problem. They have a time and throughput problem.
        </p>
      </header>

      <div className="space-y-12 text-slate-300 leading-relaxed text-lg">
        <section className="space-y-6">
          <p>Teams spend hours copying data between tools, replying to the same questions, building reports, chasing approvals, and cleaning up CRM notes. That’s not strategy. That’s friction.</p>
          <p>AI automations for business remove that friction by letting software handle the repetitive, error-prone work, while your people focus on decisions, creativity, and customer relationships.</p>
          <p>In this guide, you’ll learn what AI automations are (in plain English), where they fit alongside business process automation, real-world use cases across departments, and why many teams see faster ROI by partnering with an AI automation agency instead of going it alone.</p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter flex items-center border-b border-white/5 pb-4"><span className="text-brand-green mr-4">/01</span> WHAT ARE AI AUTOMATIONS?</h2>
          <p>An AI automation is a workflow where AI makes or improves decisions inside a process, then takes action automatically.</p>
          <div className="bg-[#0e0e0e] border border-white/5 p-8 rounded-3xl">
            <h4 className="text-brand-green font-black uppercase tracking-widest text-xs mb-4">A SIMPLE EXAMPLE</h4>
            <p className="mb-4">Instead of manually reading every inbound lead form, an AI automation can:</p>
            <ul className="space-y-3">
              {[
                "Read the form plus website/company data",
                "Score the lead and tag intent (high, medium, low)",
                "Route it to the right rep",
                "Personalize an email reply",
                "Update your CRM and notify Slack"
              ].map(item => (
                <li key={item} className="flex items-start">
                  <span className="text-brand-green mr-3 font-black">→</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter flex items-center border-b border-white/5 pb-4"><span className="text-brand-green mr-4">/02</span> AI vs. TRADITIONAL AUTOMATION</h2>
          <p>Traditional automation follows rules: <span className="text-white italic">“If this happens, then do that.”</span></p>
          <p><span className="text-brand-green font-bold">AI workflow automation</span> can also interpret, summarize, classify, predict, or generate content, then act. While Business Process Automation (BPA) moves data, AI makes sense of it.</p>
        </section>

        <section className="space-y-8">
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter flex items-center border-b border-white/5 pb-4"><span className="text-brand-green mr-4">/03</span> REAL-WORLD ROI USE CASES</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-white font-black uppercase tracking-tighter text-xl">SALES OPS</h3>
              <p className="text-slate-400 text-sm">Automate lead qualification, personalized outbound research, and CRM hygiene. Stop policing notes and start closing deals.</p>
            </div>
            <div className="space-y-4">
              <h3 className="text-white font-black uppercase tracking-tighter text-xl">MARKETING</h3>
              <p className="text-slate-400 text-sm">Turn webinars into blogs, blogs into social campaigns, and raw data into executive summaries in minutes.</p>
            </div>
            <div className="space-y-4">
              <h3 className="text-white font-black uppercase tracking-tighter text-xl">CUSTOMER SUPPORT</h3>
              <p className="text-slate-400 text-sm">Ticket triage by sentiment and urgency. Draft suggested replies using your proprietary knowledge base.</p>
            </div>
            <div className="space-y-4">
              <h3 className="text-white font-black uppercase tracking-tighter text-xl">FINANCE & OPS</h3>
              <p className="text-slate-400 text-sm">Automate invoice extraction from messy PDFs and provision internal accounts with zero manual intervention.</p>
            </div>
          </div>
        </section>

        <section className="bg-brand-green/5 border border-brand-green/10 p-12 rounded-[40px] relative overflow-hidden text-center">
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter mb-6">READY TO ARCHITECT YOUR SUCCESS?</h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">Don’t let busywork be the invisible tax on your growth. Our architects are ready to audit your ROI opportunities.</p>
          <button onClick={onContactClick} className="bg-brand-green text-black px-12 py-5 rounded-2xl text-sm font-black uppercase tracking-widest shadow-[0_0_40px_rgba(51,255,51,0.2)]">
            REQUEST AUTOMATION AUDIT
          </button>
        </section>
      </div>
    </article>
  </motion.div>
);

const CompetencyCard = ({ title, description, icon, index }: any) => (
  <motion.div custom={index} variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="bg-[#0e0e0e] border border-white/5 p-6 sm:p-8 md:p-10 rounded-[32px] sm:rounded-[40px] flex flex-col items-center text-center group hover:border-brand-green/30 transition-all duration-500 h-full">
    <div className="w-12 sm:w-16 h-12 sm:h-16 bg-[#151515] rounded-xl flex items-center justify-center mb-6 sm:mb-8 border border-white/5 group-hover:scale-110 group-hover:bg-brand-green/10 transition-all duration-300">{icon}</div>
    <h3 className="text-base sm:text-lg md:text-xl font-black tracking-tighter mb-4 text-white uppercase">{title}</h3>
    <p className="text-slate-500 text-[10px] sm:text-xs md:text-sm leading-relaxed max-w-[240px]">{description}</p>
  </motion.div>
);

const ProcessStep = ({ number, title, description, icon, index }: any) => (
  <motion.div custom={index} variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative pl-8 sm:pl-10 md:pl-12 pb-10 md:pb-12 last:pb-0 group">
    <div className="absolute left-[15px] sm:left-[19px] top-10 bottom-0 w-[1px] bg-white/5 group-last:hidden" />
    <div className="absolute left-0 top-0 w-8 sm:w-10 h-8 sm:h-10 rounded-full border border-white/10 bg-[#0e0e0e] flex items-center justify-center text-brand-green font-black text-[10px] z-10 group-hover:border-brand-green/50 transition-colors">{number}</div>
    <div className="pt-2"><h4 className="text-sm sm:text-base md:text-lg font-black tracking-tighter text-white uppercase mb-3 flex items-center"><span className="mr-2 sm:mr-3 opacity-30 group-hover:opacity-100 group-hover:text-brand-green transition-all">{icon}</span>{title}</h4><p className="text-slate-500 text-[10px] sm:text-xs md:text-sm leading-relaxed max-w-xl">{description}</p></div>
  </motion.div>
);

const PortfolioCard = ({ project, onOpen, index }: any) => (
  <motion.div custom={index} variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} onClick={() => onOpen(project)} className="bg-[#0e0e0e] border border-white/5 rounded-[32px] sm:rounded-[40px] overflow-hidden group hover:border-brand-green/30 transition-all duration-500 cursor-pointer flex flex-col h-full">
    <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden"><img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /><div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" /></div>
    <div className="p-6 sm:p-8 md:p-10 flex flex-col flex-grow"><h3 className="text-sm sm:text-base md:text-lg font-black tracking-tighter mb-4 text-white uppercase leading-tight">{project.title}</h3><p className="text-slate-400 text-[10px] sm:text-xs md:text-sm leading-relaxed mb-8 flex-grow">{project.description}</p><div className="flex flex-wrap gap-2 mt-auto">{project.tags.map((tag: string) => (<span key={tag} className="text-[7px] md:text-[8px] font-black uppercase tracking-widest text-slate-500 border border-white/5 bg-white/5 px-2 py-1 rounded-full group-hover:border-brand-green/20 group-hover:text-brand-green transition-colors">{tag}</span>))}</div></div>
  </motion.div>
);

const MetricCard = ({ title, quote, impact, date, onClick }: any) => (
  <div onClick={onClick} className="bg-[#0e0e0e] border border-white/5 p-6 sm:p-8 rounded-3xl min-w-[280px] sm:min-w-[300px] md:min-w-[340px] max-w-[280px] sm:max-w-[300px] md:max-w-[340px] relative overflow-hidden group select-none cursor-pointer hover:border-brand-green/30 transition-all duration-300">
    <div className="flex justify-between items-start mb-6"><div className="flex space-x-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 sm:w-4 h-3 sm:h-4 text-brand-green fill-brand-green" />)}</div><span className="text-[8px] sm:text-[9px] font-bold text-slate-500">{date}</span></div>
    <h4 className="text-[9px] sm:text-[10px] md:text-xs font-black tracking-tighter mb-6 uppercase h-10 leading-relaxed text-white">{title}</h4>
    <div className="mb-8 sm:mb-10 relative"><span className="absolute -top-4 -left-2 text-3xl sm:text-4xl text-brand-green/10 font-serif">"</span><p className="text-slate-400 text-[10px] sm:text-xs md:text-[13px] leading-relaxed line-clamp-4">{quote}</p></div>
    <div className="border-t border-white/5 pt-6 flex items-center justify-between"><div><span className="block text-[7px] sm:text-[8px] font-black text-brand-green uppercase tracking-wider mb-1">MEASURED IMPACT</span><span className="text-[9px] sm:text-[10px] md:text-[11px] font-bold text-slate-300">{impact}</span></div><div className="w-7 sm:w-8 h-7 sm:h-8 rounded-lg bg-[#151515] flex items-center justify-center border border-white/10 group-hover:bg-brand-green/10 transition"><Maximize2 className="w-3 sm:w-4 h-3 sm:h-4 text-slate-600 group-hover:text-brand-green transition" /></div></div>
  </div>
);

export default function App() {
  const [velocity, setVelocity] = useState(50);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [view, setView] = useState<'home' | 'blog'>('home');

  const scrollDuration = useMemo(() => {
    const minDuration = 4;
    const maxDuration = 60;
    const factor = (100 - velocity) / 100;
    return minDuration + (factor * (maxDuration - minDuration));
  }, [velocity]);

  const marqueeItems = useMemo(() => [...METRICS, ...METRICS, ...METRICS, ...METRICS], []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [view]);

  // Styling for ElevenLabs Widget
  useEffect(() => {
    const injectStyles = () => {
      const widget = document.querySelector('elevenlabs-convai') as any;
      if (widget && widget.shadowRoot && !widget.shadowRoot.getElementById('custom-neon-theme')) {
        const style = document.createElement('style');
        style.id = 'custom-neon-theme';
        style.textContent = `
          :host {
            --primary: #33ff33;
            --bg-color: #0a0a0a;
            --text-color: #ffffff;
            --border-color: rgba(51, 255, 51, 0.3);
            font-family: 'Inter', sans-serif !important;
          }
          .card, [class*="card"] {
            background-color: #0a0a0a !important;
            border: 1px solid rgba(51, 255, 51, 0.3) !important;
            border-radius: 24px !important;
            box-shadow: 0 0 30px rgba(51, 255, 51, 0.15) !important;
            color: white !important;
          }
          button {
            background-color: #33ff33 !important;
            color: black !important;
            font-weight: 800 !important;
            text-transform: uppercase !important;
            border-radius: 99px !important;
          }
          input {
            background-color: #111 !important;
            color: white !important;
            border: 1px solid #333 !important;
            border-radius: 12px !important;
          }
          input:focus {
            border-color: #33ff33 !important;
          }
        `;
        widget.shadowRoot.appendChild(style);
      }
    };
    
    // Check every second to ensure style is injected if widget re-renders
    const interval = setInterval(injectStyles, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="selection:bg-brand-green selection:text-black">
      <Header onContactClick={() => setIsModalOpen(true)} setView={setView} />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ProjectModal project={selectedProject} isOpen={!!selectedProject} onClose={() => setSelectedProject(null)} />
      <MetricModal metric={selectedMetric} isOpen={!!selectedMetric} onClose={() => setSelectedMetric(null)} />

      <AnimatePresence mode="wait">
        {view === 'home' ? (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Hero Section */}
            <section className="relative min-h-screen flex flex-col items-center justify-center pt-32 pb-12 px-4 sm:px-6 grid-pattern overflow-hidden">
              <div className="absolute top-1/4 left-1/4 w-[200px] sm:w-[500px] h-[200px] sm:h-[500px] bg-brand-green/5 blur-[60px] sm:blur-[120px] rounded-full -z-10 animate-drift"></div>
              <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-7xl mx-auto text-center relative w-full flex flex-col items-center justify-center">
                <motion.div variants={fadeIn} className="inline-block border border-brand-green/30 bg-brand-green/5 px-4 py-1.5 rounded-full mb-8 shadow-[0_0_15px_rgba(51,255,51,0.1)]"><span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em] text-brand-green">ENTERPRISE ARCHITECTURE</span></motion.div>
                <motion.h1 variants={fadeIn} className="w-full flex flex-col items-center justify-center text-center text-3xl sm:text-5xl md:text-[80px] lg:text-[95px] font-black leading-[1] tracking-tighter mb-8 text-white uppercase"><span className="block whitespace-nowrap text-center">DON’T JUST BUILD,</span><motion.span className="text-brand-green neon-text block mt-2 whitespace-nowrap text-center" animate={{ filter: ["brightness(1)", "brightness(1.3)", "brightness(1)"] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}><TypingWord word="ARCHITECT REALITY." /></motion.span></motion.h1>
                <motion.p variants={fadeIn} className="text-slate-400 text-sm sm:text-lg lg:text-xl max-w-4xl mx-auto mb-10 font-medium leading-relaxed px-2 flex flex-col items-center text-center"><span>We build high performance AI systems that give power back to you.</span><span className="hidden sm:inline">Transforming business complexity into real results at prices that actually make sense.</span></motion.p>
                <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 w-full">
                  <motion.button 
                    whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(51, 255, 51, 0.4)" }} 
                    whileTap={{ scale: 0.98 }} 
                    onClick={() => setIsModalOpen(true)} 
                    className="w-full sm:w-auto bg-brand-green text-black px-8 md:px-10 py-4 md:py-5 rounded-2xl text-[10px] sm:text-sm font-black uppercase tracking-widest hover:scale-105 transition-all shadow-[0_0_40px_rgba(51,255,51,0.3)] flex items-center justify-center group"
                  >
                    ARCHITECT MY SOLUTION
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.05, color: '#33ff33' }} onClick={() => setView('blog')} className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-white transition flex items-center group py-2">
                    RESOURCES & INSIGHTS
                    <FileText className="ml-2 w-4 h-4 group-hover:rotate-12 transition-transform" />
                  </motion.button>
                </motion.div>
                <TrustBadges />
              </motion.div>
            </section>

            {/* Architecture Section */}
            <section id="architecture" className="py-20 md:py-32 px-4 sm:px-6 bg-black">
              <div className="max-w-7xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16 md:mb-24">
                  <span className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-green mb-4 block">CORE COMPETENCIES</span>
                  <h2 className="text-3xl sm:text-5xl md:text-7xl font-black tracking-tighter text-white uppercase">
                    CORE <span className="text-brand-green">ARCHITECTURE</span>
                  </h2>
                </motion.div>
                <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                  {COMPETENCIES.map((c, i) => <CompetencyCard key={i} index={i} {...c} />)}
                </motion.div>
              </div>
            </section>

            {/* Process Section */}
            <section id="process" className="py-20 md:py-32 px-4 sm:px-6 bg-[#050505] border-y border-white/5 overflow-hidden">
              <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
                <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:w-1/3 lg:sticky lg:top-32 z-10 bg-[#050505] lg:bg-transparent pb-4 md:pb-8 lg:pb-0">
                  <span className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-green mb-4 block">SYSTEM FLOW</span>
                  <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter text-white uppercase mb-6 md:mb-8">
                    THE <br className="hidden lg:block" /><span className="text-brand-green">ARCHITECTURE</span> <br className="hidden lg:block" />PROCESS
                  </h2>
                  <p className="text-slate-400 text-[10px] sm:text-xs md:text-sm leading-relaxed mb-8 md:mb-10 max-w-sm">Our architecture first methodology ensures high quality, scalable AI solutions that transform enterprise complexity into measurable mission critical outcomes.</p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 text-brand-green bg-brand-green/5 border border-brand-green/10 p-4 rounded-[20px]">
                      <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                      <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest leading-snug text-center">PROACTIVE 3 DAY DELAY COMMUNICATION PROTOCOL</span>
                    </div>
                  </div>
                </motion.div>
                <div className="lg:w-2/3 w-full">
                  {PROCESS_STEPS.map((step, i) => (<ProcessStep key={i} index={i} {...step} />))}
                </div>
              </div>
            </section>

            {/* Portfolio Section */}
            <section id="portfolio" className="py-20 md:py-32 px-4 sm:px-6 bg-black border-t border-white/5">
              <div className="max-w-7xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16 md:mb-24">
                  <h2 className="text-3xl sm:text-5xl md:text-[80px] font-black tracking-tighter text-white uppercase mb-4 leading-none text-center">THE <span className="text-brand-green">PORTFOLIO</span></h2>
                  <p className="text-slate-400 text-[10px] sm:text-sm max-w-2xl mx-auto uppercase tracking-widest font-bold leading-relaxed px-2 break-words text-center">These are not projects; they are architected solutions engineered to drive measurable, mission critical business outcomes.</p>
                </motion.div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
                  {PORTFOLIO.map((project, i) => (<PortfolioCard key={i} index={i} project={project} onOpen={(p: any) => setSelectedProject(p)} />))}
                </div>
              </div>
            </section>

            {/* Metrics Section */}
            <section id="excellence" className="py-20 md:py-32 bg-black border-y border-white/5 relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none"></div>
              <div className="max-w-full mx-auto relative overflow-hidden">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="text-center mb-12 px-6 flex flex-col items-center">
                  <span className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-green mb-4 block text-center">SOCIAL PROOF</span>
                  <h2 className="text-3xl sm:text-5xl md:text-7xl font-black tracking-tighter text-white uppercase mb-8 text-center">SUCCESS <span className="text-brand-green neon-text">METRICS</span></h2>
                  <p className="text-slate-400 text-[10px] sm:text-xs md:text-sm max-w-2xl mx-auto mb-16 font-medium leading-relaxed text-center">We deliver measurable digital dominance through architectures engineered for scale, stability, and high impact revenue growth.</p>
                  <div className="max-w-md mx-auto mb-20 px-4 w-full">
                    <div className="flex justify-between items-end mb-3"><span className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">VELOCITY</span><motion.span animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }} className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-brand-green">[ {velocity < 30 ? 'IDLE' : velocity < 70 ? 'STABLE' : velocity < 95 ? 'FAST' : 'TURBO'} ]</motion.span></div>
                    <div className="relative h-8 flex items-center group">
                      <div className="absolute w-full h-[2px] bg-slate-900 rounded-full"></div>
                      <motion.div initial={false} animate={{ width: `${velocity}%` }} className="absolute top-1/2 -translate-y-1/2 h-[2px] bg-brand-green"></motion.div>
                      <input type="range" min="5" max="100" value={velocity} onChange={(e) => setVelocity(parseInt(e.target.value))} className="absolute w-full h-full opacity-0 cursor-pointer z-20" />
                      <motion.div initial={false} animate={{ left: `${velocity}%` }} className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-brand-green rounded-full shadow-[0_0_15px_rgba(51,255,51,0.8)] z-10 pointer-events-none -translate-x-1/2 border border-black"></motion.div>
                    </div>
                  </div>
                </motion.div>
                <div className="relative w-full overflow-hidden py-10" onMouseEnter={() => setIsCarouselPaused(true)} onMouseLeave={() => setIsCarouselPaused(false)}>
                  <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-r from-black via-black/80 to-transparent z-10 pointer-events-none"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-l from-black via-black/80 to-transparent z-10 pointer-events-none"></div>
                  <div className="marquee-container space-x-4 sm:space-x-8 px-4" style={{ animationDuration: `${scrollDuration}s`, animationPlayState: isCarouselPaused ? 'paused' : 'running' }}>
                    {marqueeItems.map((m, i) => <div key={i} className="flex-shrink-0"><MetricCard onClick={() => setSelectedMetric(m)} {...m} /></div>)}
                  </div>
                </div>
              </div>
            </section>

            {/* Footer containing CTA and Form */}
            <footer className="bg-[#050505] pt-20 md:pt-32 pb-12 px-4 sm:px-6 border-t border-white/5 relative overflow-hidden">
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-brand-green/5 blur-[100px] rounded-full -z-10"></div>
              <div className="max-w-7xl mx-auto text-center relative flex flex-col items-center">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="mb-32 w-full flex flex-col items-center">
                  <h3 className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-black tracking-tighter mb-10 md:mb-12 uppercase leading-tight text-white px-2 break-words text-center w-full">
                    STOP BUILDING BRITTLE AUTOMATIONS.<br />
                    <span className="text-brand-green neon-text text-center block">START ARCHITECTING INTELLIGENCE.</span>
                  </h3>
                  <motion.button whileHover={{ scale: 1.05, filter: "brightness(1.1)" }} whileTap={{ scale: 0.95 }} onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto bg-brand-green text-black px-8 sm:px-12 py-4 md:py-6 rounded-2xl text-xs sm:text-lg font-black uppercase tracking-widest hover:brightness-110 transition shadow-[0_0_50px_rgba(51,255,51,0.2)]">
                    SCHEDULE A CALL
                  </motion.button>
                </motion.div>

                <section id="insights-form" className="w-full py-24 border-t border-white/5 mb-32 flex flex-col items-center">
                   <div className="max-w-5xl mx-auto text-center relative z-10 w-full flex flex-col items-center px-4">
                      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="w-full flex flex-col items-center">
                        <QualificationForm />
                      </motion.div>
                   </div>
                </section>

                <div className="flex flex-col md:flex-row items-center justify-between pt-12 border-t border-white/5 gap-8 text-slate-500 w-full">
                  <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    <motion.img whileHover={{ rotate: 360 }} transition={{ duration: 1 }} src={LOGO_URL} alt="Meta Know Labs Logo" className="w-8 h-8 object-contain" />
                    <span className="text-[10px] sm:text-[12px] font-black tracking-tighter uppercase group-hover:text-brand-green transition">META KNOW LABS</span>
                  </div>
                  <div className="flex flex-wrap justify-center gap-6 sm:gap-12 text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em]">
                    <a href="https://www.linkedin.com/in/devon-j-williams/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-all hover:tracking-[0.3em]">LinkedIn</a>
                    <a href={UPWORK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-all hover:tracking-[0.3em]">Upwork</a>
                    <button onClick={() => setView('blog')} className="hover:text-white transition-all hover:tracking-[0.3em] uppercase">Insights</button>
                  </div>
                  <div className="text-[8px] sm:text-[10px] font-bold tracking-widest uppercase opacity-30 text-center md:text-right">&copy; 2025 MKL ARCHITECTURE SYSTEMS</div>
                </div>
              </div>
            </footer>
          </motion.div>
        ) : (
          <BlogView setView={setView} onContactClick={() => setIsModalOpen(true)} />
        )}
      </AnimatePresence>
    </div>
  );
}